import { useToast } from '@/composables/useToast';
import { STRIPE_PUBLISHED_KEY } from '@/data/env';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import type { StripeExpressCheckoutElement, StripePaymentElementOptions } from '@stripe/stripe-js';
import { onUnmounted, ref, type Ref } from 'vue';

/** Trim and return undefined for empty/non-string values, for Stripe billingDetails defaults. */
function billingString(value: string | null | undefined): string | undefined {
  if (value == null || typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Owns the Stripe Elements lifecycle for the checkout flow.
 *
 *  - `init()` loads Stripe, creates a SetupIntent, and mounts the payment +
 *    express-checkout elements into the provided container refs.
 *  - `submit()` validates the payment element, confirms the setup, then calls
 *    `onConfirmed` (for the Subscribe button flow).
 *  - The express-checkout `confirm` event automatically runs the same
 *    confirm-then-`onConfirmed` flow (express has its own UI for validation).
 *  - `isInitializing` / `isPaymentComplete` / `isSubmitting` are exposed for
 *    the host to drive button state.
 *  - Tears down via `paymentStore.teardownPaymentForm()` on component unmount.
 */
export function useStripeCheckout(options: {
  paymentContainer: Ref<HTMLDivElement | null>;
  expressContainer: Ref<HTMLDivElement | null>;
  getReturnUrl: () => string;
  onConfirmed: () => Promise<void>;
}) {
  const billingStore = useBillingStore();
  const paymentStore = usePaymentStore();
  const userStore = useUserStore();
  const { showToast } = useToast();
  const { t } = useI18n();

  const isInitializing = ref(true);
  const isPaymentComplete = ref(false);
  const isSubmitting = ref(false);
  const alive = ref(true);

  // expressEl is owned here (not the paymentStore) because nothing outside this
  // composable needs to read it. Tracked so we can .destroy() it on unmount —
  // otherwise the DOM node and its 'confirm' listener leak across nav cycles.
  let expressEl: StripeExpressCheckoutElement | null = null;

  async function runConfirm(): Promise<void> {
    await paymentStore.confirmSetup(options.getReturnUrl());
    if (alive.value) await options.onConfirmed();
  }

  async function init(): Promise<void> {
    paymentStore.clearError();
    isInitializing.value = true;
    isPaymentComplete.value = false;
    try {
      const stripe = await paymentStore.initializeStripe(STRIPE_PUBLISHED_KEY);
      if (!alive.value) return;

      const clientSecret = await paymentStore.createSetupIntent();
      if (!alive.value) return;

      const customerSessionClientSecret = paymentStore.customerSessionClientSecret || undefined;
      const elements = stripe.elements({
        clientSecret,
        ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
        loader: 'auto',
        appearance: { theme: 'stripe' },
      });
      paymentStore.setElements(elements);

      const billingName = billingString(billingStore.customer?.name) ?? billingString(userStore.user.name);
      const billingEmail = billingString(billingStore.customer?.email) ?? billingString(userStore.user.email);

      const paymentOptions: StripePaymentElementOptions = {
        defaultValues: {
          billingDetails: {
            ...(billingName ? { name: billingName } : {}),
            ...(billingEmail ? { email: billingEmail } : {}),
          },
        },
        wallets: { applePay: 'auto', googlePay: 'auto' },
      };

      const paymentEl = elements.create('payment', paymentOptions);
      paymentStore.setPaymentElement(paymentEl);

      paymentEl.on('change', (event) => {
        isPaymentComplete.value = event.complete;
      });

      if (options.paymentContainer.value && alive.value) {
        paymentEl.mount(options.paymentContainer.value);
      }

      expressEl = elements.create('expressCheckout', {
        paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'auto', paypal: 'auto' },
      });
      expressEl.on('confirm', async () => {
        if (isSubmitting.value) return;
        isSubmitting.value = true;
        paymentStore.clearError();
        try {
          await runConfirm();
        } catch (err) {
          if (!paymentStore.error) {
            showToast('danger', err instanceof Error ? err.message : t('payment_error_unexpected'));
          }
        } finally {
          isSubmitting.value = false;
        }
      });
      if (options.expressContainer.value && alive.value) {
        expressEl.mount(options.expressContainer.value);
      }
    } catch (err) {
      logError('Failed to initialize payment form:', err);
      showToast('danger', t('payment_error_unexpected'));
    } finally {
      isInitializing.value = false;
    }
  }

  async function submit(): Promise<void> {
    if (isSubmitting.value) return;
    const elements = paymentStore.elements;
    if (!elements) return;

    isSubmitting.value = true;
    paymentStore.clearError();
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        if (submitError.type === 'card_error' || submitError.type === 'validation_error') {
          showToast('danger', submitError.message ?? t('payment_error_unexpected'));
        } else {
          showToast('danger', t('payment_error_unexpected'));
        }
        return;
      }
      await runConfirm();
    } catch (err) {
      if (!paymentStore.error) {
        showToast('danger', err instanceof Error ? err.message : t('payment_error_unexpected'));
      }
    } finally {
      isSubmitting.value = false;
    }
  }

  onUnmounted(() => {
    alive.value = false;
    try {
      expressEl?.destroy();
    } catch {
      /* ignore — Stripe sometimes throws if already destroyed */
    }
    expressEl = null;
    paymentStore.teardownPaymentForm();
  });

  return { isInitializing, isPaymentComplete, isSubmitting, init, submit };
}
